import type { App, Component, VNode } from 'vue'
import { createApp, h } from 'vue'

import type { RenderCallbackContent } from '../types/cell-content'

/**
 * Options for creating a Vue cell
 */
export interface VueCellOptions {
  /**
   * Vue component to render
   */
  component: Component

  /**
   * Props to pass to the component
   */
  props?: Record<string, any>

  /**
   * Event listeners to attach to the component
   * @example
   * {
   *   onClick: (e) => console.log('clicked', e),
   *   onUpdate: (value) => console.log('updated', value)
   * }
   */
  on?: Record<string, (...args: any[]) => void>

  /**
   * Children/slots to pass to the component
   */
  children?: VNode | VNode[] | string
}

/**
 * Create a datatable cell that renders a Vue component
 *
 * This helper manages the Vue app lifecycle, mounting and unmounting
 * the component when the cell is added/removed from the table.
 *
 * @param options - Vue cell options
 * @returns Render callback content for the datatable
 *
 * @example
 * ```typescript
 * import { createVueCell } from 'dap-design-system/helpers/vue'
 * import StatusBadge from './StatusBadge.vue'
 *
 * const columns = [
 *   {
 *     id: 'status',
 *     header: 'Status',
 *     cell: (row) => createVueCell({
 *       component: StatusBadge,
 *       props: {
 *         status: row.status,
 *         variant: 'success'
 *       },
 *       on: {
 *         onClick: () => console.log('Badge clicked')
 *       }
 *     })
 *   }
 * ]
 * ```
 */
export function createVueCell(options: VueCellOptions): RenderCallbackContent
export function createVueCell(
  component: Component,
  props?: Record<string, any>,
): RenderCallbackContent

export function createVueCell(
  componentOrOptions: Component | VueCellOptions,
  propsArg?: Record<string, any>,
): RenderCallbackContent {
  // Normalize arguments - check if first arg is VueCellOptions
  const isOptions = (
    arg: Component | VueCellOptions,
  ): arg is VueCellOptions => {
    return (
      typeof arg === 'object' &&
      arg !== null &&
      'component' in arg &&
      typeof arg.component !== 'undefined'
    )
  }

  const options: VueCellOptions = isOptions(componentOrOptions)
    ? componentOrOptions
    : {
        component: componentOrOptions as Component,
        props: propsArg,
      }

  const { component, props, on, children } = options

  // Track the Vue app instance
  let app: App | null = null

  return {
    render: (container: HTMLElement) => {
      // Create Vue app with render function
      app = createApp({
        render() {
          // Build event handlers object
          const eventHandlers: Record<string, any> = {}
          if (on) {
            Object.entries(on).forEach(([event, handler]) => {
              // Vue expects event handlers in the format: onEventName
              const eventKey = `on${event.charAt(0).toUpperCase()}${event.slice(1)}`
              eventHandlers[eventKey] = handler
            })
          }

          // Render component with props, events, and children
          return h(
            component,
            {
              ...props,
              ...eventHandlers,
            },
            children,
          )
        },
      })

      // Mount the app to the container
      try {
        if (app) {
          app.mount(container)
        } else {
          throw new Error('Failed to create Vue app')
        }
      } catch (error) {
        console.error('Error mounting Vue component in datatable cell:', error)
        container.textContent = 'Error rendering cell'
      }
    },

    cleanup: () => {
      // Unmount the Vue app
      if (app !== null) {
        try {
          app.unmount()
        } catch (error) {
          console.error(
            'Error unmounting Vue component in datatable cell:',
            error,
          )
        } finally {
          app = null
        }
      }
    },
  }
}

/**
 * Type helper for creating type-safe cell renderers
 *
 * @example
 * ```typescript
 * import type { VueCellRenderer } from 'dap-design-system/helpers/vue'
 *
 * interface User {
 *   id: number
 *   status: string
 * }
 *
 * const statusCell: VueCellRenderer<User> = (row) => createVueCell({
 *   component: StatusBadge,
 *   props: { status: row.status }
 * })
 * ```
 */
export type VueCellRenderer<TData = any> = (row: TData) => RenderCallbackContent
